import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { NavLink, useLocation } from "react-router-dom";
import axios from "axios";
import TuvSud from "../images/tuv-sud.png";
import "./Agents.css";
import ConformitySection from "./ConformitySection";

const CATEGORIES = ["Всички", "Соларни панели", "Фасади", "Индустриални"];

const COLORS = {
  primary: "#0D3B66",
  secondary: "#0061A6",
  text: "#273649",
  muted: "#64748B",
  border: "#DFE7F0",
  background: "#F5F8FC",
  white: "#FFFFFF",
};

function getPublicPath(path) {
  if (!path || typeof path !== "string") return "";

  const value = path.trim();

  // Keep absolute URLs intact.
  if (/^(https?:)?\/\//i.test(value) || value.startsWith("data:")) {
    return value;
  }

  return `/${value.replace(/^(?:\.\/|\/)+/, "")}`;
}

function ShimmerCard() {
  return (
    <Box
      aria-hidden="true"
      sx={{
        minWidth: 0,
        overflow: "hidden",
        border: `1px solid ${COLORS.border}`,
        borderRadius: 3,
        bgcolor: COLORS.white,
      }}
    >
      <Box
        sx={{
          height: { xs: 145, sm: 190, md: 220 },
          background:
            "linear-gradient(100deg, #F0F3F6 20%, #E1E7ED 40%, #F0F3F6 60%)",
          backgroundSize: "200% 100%",
          animation: "catalogShimmer 1.5s ease-in-out infinite",
          "@keyframes catalogShimmer": {
            "0%": { backgroundPosition: "100% 0" },
            "100%": { backgroundPosition: "-100% 0" },
          },
        }}
      />
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            height: 14,
            width: "75%",
            mx: "auto",
            borderRadius: 1,
            bgcolor: "#E9EEF3",
          }}
        />
        <Box
          sx={{
            height: 10,
            width: "45%",
            mx: "auto",
            mt: 1.5,
            borderRadius: 1,
            bgcolor: "#F0F3F6",
          }}
        />
      </Box>
    </Box>
  );
}

function ProductImage({ src, name }) {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [src]);

  if (!src || failed) {
    return (
      <Box
        role="img"
        aria-label={name || "Изображение на продукт"}
        sx={{
          height: "100%",
          minHeight: 145,
          display: "grid",
          placeItems: "center",
          bgcolor: "#F2F6FA",
          color: COLORS.muted,
          p: 2,
          textAlign: "center",
        }}
      >
        <Typography variant="body2">{name || "Продукт"}</Typography>
      </Box>
    );
  }

  return (
    <CardMedia
      component="img"
      image={src}
      alt={name || "Продукт"}
      loading="lazy"
      onError={() => setFailed(true)}
      sx={{
        width: "100%",
        height: "100%",
        minHeight: 145,
        maxHeight: { xs: 175, sm: 220, md: 235 },
        objectFit: "contain",
        p: { xs: 1, sm: 1.5 },
        boxSizing: "border-box",
        transition: "transform 220ms ease",
        ".MuiCard-root:hover &": {
          transform: "scale(1.035)",
        },
      }}
    />
  );
}

function ResponsiveTabs({ selectedTab, onChange, isMobile }) {
  return (
    <Box
      sx={{
        width: "100%",
        overflow: "hidden",
        borderBottom: { xs: "none", sm: `1px solid ${COLORS.border}` },
      }}
    >
      <Tabs
        value={selectedTab}
        onChange={(_, value) => onChange(value)}
        variant={isMobile ? "fullWidth" : "scrollable"}
        scrollButtons={!isMobile}
        allowScrollButtonsMobile
        aria-label="Категории препарати"
        sx={{
          minHeight: 46,
          "& .MuiTab-root": {
            minHeight: 46,
            minWidth: { xs: 0, sm: 120 },
            px: { xs: 0.5, sm: 2 },
            fontSize: { xs: "0.72rem", sm: "0.875rem" },
            lineHeight: 1.25,
            textTransform: "none",
            fontWeight: 600,
            color: COLORS.muted,
          },
          "& .Mui-selected": {
            color: `${COLORS.primary} !important`,
            fontWeight: 800,
          },
          "& .MuiTabs-indicator": {
            height: 3,
            borderRadius: "3px 3px 0 0",
            backgroundColor: COLORS.secondary,
          },
        }}
      >
        {CATEGORIES.map((category) => (
          <Tab
            key={category}
            label={category}
            id={`catalog-tab-${CATEGORIES.indexOf(category)}`}
            aria-controls={`catalog-panel-${CATEGORIES.indexOf(category)}`}
          />
        ))}
      </Tabs>
    </Box>
  );
}

export default function Agents() {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedTab, setSelectedTab] = useState(0);

  // Restore the selected category when returning from a product page.
  useEffect(() => {
    const requestedTab = location.state?.selectedTab;

    if (
      Number.isInteger(requestedTab) &&
      requestedTab >= 0 &&
      requestedTab < CATEGORIES.length
    ) {
      setSelectedTab(requestedTab);
    }
  }, [location.state]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadAgents() {
      setLoading(true);
      setError("");

      try {
        const response = await axios.get("/db/agents.json");

        if (!Array.isArray(response.data)) {
          throw new Error("Невалиден формат на продуктовия каталог.");
        }

        if (!cancelled) {
          setAgents(response.data);
        }
      } catch (err) {
        console.error("Грешка при зареждане на препаратите:", err);

        if (!cancelled) {
          setError("Не успяхме да заредим каталога. Моля, опитай отново.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadAgents();

    return () => {
      cancelled = true;
    };
  }, []);

  // Catalog page metadata.
  useEffect(() => {
    const previousTitle = document.title;
    document.title = "Препарати за почистване на соларни панели | TC Energy";

    let descriptionMeta = document.querySelector('meta[name="description"]');
    const createdMeta = !descriptionMeta;

    if (!descriptionMeta) {
      descriptionMeta = document.createElement("meta");
      descriptionMeta.name = "description";
      document.head.appendChild(descriptionMeta);
    }

    const previousDescription = descriptionMeta.getAttribute("content");

    descriptionMeta.setAttribute(
      "content",
      "Разгледайте професионалните препарати ChemiTek за почистване и поддръжка на соларни панели. Вижте продуктовата информация и наличните опаковки от TC Energy.",
    );

    return () => {
      document.title = previousTitle;

      if (createdMeta) {
        descriptionMeta.remove();
      } else if (previousDescription !== null) {
        descriptionMeta.setAttribute("content", previousDescription);
      }
    };
  }, []);

  const filteredAgents =
    selectedTab === 0
      ? agents
      : agents.filter((agent) => agent.category === CATEGORIES[selectedTab]);

  const retryLoading = () => {
    // Trigger a page reload if the catalog could not be loaded.
    window.location.reload();
  };

  return (
    <Box
      component="main"
      sx={{
        minHeight: "60vh",
        bgcolor: COLORS.background,
        py: { xs: 2.5, sm: 4, md: 5 },
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 1320,
          mx: "auto",
          px: { xs: 1.5, sm: 3, md: 4 },
          boxSizing: "border-box",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            maxWidth: 850,
            mx: "auto",
            mb: { xs: 2.5, md: 3.5 },
            textAlign: { xs: "left", sm: "center" },
          }}
        >
          <Typography
            component="h1"
            sx={{
              color: COLORS.primary,
              fontSize: {
                xs: "1.65rem",
                sm: "2.1rem",
                md: "2.5rem",
              },
              fontWeight: 900,
              lineHeight: 1.2,
              letterSpacing: { xs: "-0.025em", sm: "-0.035em" },
              mb: 2,
            }}
          >
            Препарати за почистване
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: COLORS.text,
              lineHeight: 1.8,
              fontSize: { xs: "0.93rem", sm: "1rem" },
            }}
          >
            Препаратите на <strong>ChemiTek</strong> са предназначени за
            професионална поддръжка и почистване на соларни панели и други
            повърхности според предназначението на конкретния продукт.
          </Typography>

          <Typography
            variant="body2"
            sx={{
              mt: 1,
              color: COLORS.muted,
              lineHeight: 1.75,
            }}
          >
            Разгледайте каталога и изберете продукт, за да видите неговите
            характеристики, опаковки и цени.
          </Typography>
        </Box>

        {/* Categories */}
        <Box
          sx={{
            mb: { xs: 2.5, sm: 3 },
            p: { xs: 0.5, sm: 0 },
            border: { xs: `1px solid ${COLORS.border}`, sm: "none" },
            borderRadius: { xs: 2, sm: 0 },
            bgcolor: { xs: COLORS.white, sm: "transparent" },
          }}
        >
          <ResponsiveTabs
            selectedTab={selectedTab}
            onChange={setSelectedTab}
            isMobile={isMobile}
          />
        </Box>

        {/* Loading state */}
        {loading && (
          <Box
            aria-label="Зареждане на продуктите"
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, minmax(0, 1fr))",
                sm: "repeat(2, minmax(0, 1fr))",
                md: "repeat(3, minmax(0, 1fr))",
                lg: "repeat(4, minmax(0, 1fr))",
              },
              gap: { xs: 1.5, sm: 2.5, md: 3 },
            }}
          >
            {Array.from({ length: 8 }).map((_, index) => (
              <ShimmerCard key={index} />
            ))}
          </Box>
        )}

        {/* Error state */}
        {!loading && error && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={retryLoading}>
                Опитай отново
              </Button>
            }
            sx={{ borderRadius: 2 }}
          >
            {error}
          </Alert>
        )}

        {/* Empty state */}
        {!loading && !error && filteredAgents.length === 0 && (
          <Box
            sx={{
              py: 6,
              px: 2,
              textAlign: "center",
              bgcolor: COLORS.white,
              border: `1px solid ${COLORS.border}`,
              borderRadius: 3,
            }}
          >
            <Typography
              variant="h6"
              sx={{ color: COLORS.primary, fontWeight: 800 }}
            >
              Няма намерени продукти
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, color: COLORS.muted }}>
              В тази категория все още няма добавени препарати.
            </Typography>
          </Box>
        )}

        {/* Responsive product grid */}
        {!loading && !error && filteredAgents.length > 0 && (
          <Box
            id={`catalog-panel-${selectedTab}`}
            role="tabpanel"
            aria-labelledby={`catalog-tab-${selectedTab}`}
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, minmax(0, 1fr))",
                sm: "repeat(2, minmax(0, 1fr))",
                md: "repeat(3, minmax(0, 1fr))",
                lg: "repeat(4, minmax(0, 1fr))",
              },
              gap: { xs: 1.5, sm: 2.5, md: 3 },
              alignItems: "stretch",
            }}
          >
            {filteredAgents.map((agent) => {
              const name = agent.nameBulgarian || agent.name || "Продукт";
              const imageSrc = getPublicPath(agent.image);

              return (
                <Card
                  key={agent.id}
                  component={NavLink}
                  to={`/agentInfo/${encodeURIComponent(String(agent.id))}`}
                  state={{ agent, tab: selectedTab }}
                  aria-label={`Виж продукта: ${name}`}
                  elevation={0}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    minWidth: 0,
                    height: "100%",
                    overflow: "hidden",
                    textDecoration: "none",
                    color: "inherit",
                    bgcolor: COLORS.white,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: { xs: 2.5, sm: 3 },
                    boxShadow: "0 2px 8px rgba(13, 59, 102, 0.035)",
                    transition:
                      "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease",
                    "&:hover": {
                      transform: { xs: "none", sm: "translateY(-3px)" },
                      borderColor: "#B8D0E7",
                      boxShadow: "0 10px 26px rgba(13, 59, 102, 0.09)",
                    },
                    "&:focus-visible": {
                      outline: `3px solid ${COLORS.secondary}`,
                      outlineOffset: 3,
                    },
                  }}
                >
                  {/* Image area */}
                  <Box
                    sx={{
                      position: "relative",
                      height: { xs: 145, sm: 190, md: 220 },
                      overflow: "hidden",
                      bgcolor: "#F8FAFC",
                      borderBottom: `1px solid ${COLORS.border}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <ProductImage src={imageSrc} name={name} />
                  </Box>

                  {/* Product name */}
                  <CardContent
                    sx={{
                      p: { xs: 1.25, sm: 2 },
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      "&:last-child": {
                        pb: { xs: 1.5, sm: 2 },
                      },
                    }}
                  >
                    <Typography
                      component="h2"
                      sx={{
                        width: "100%",
                        color: COLORS.primary,
                        textAlign: "center",
                        fontSize: {
                          xs: "0.83rem",
                          sm: "0.98rem",
                          md: "1.02rem",
                        },
                        fontWeight: 750,
                        lineHeight: 1.5,
                        overflowWrap: "anywhere",
                        display: "-webkit-box",
                        WebkitBoxOrient: "vertical",
                        WebkitLineClamp: 3,
                        overflow: "hidden",
                      }}
                    >
                      {name}
                    </Typography>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        )}
        <ConformitySection />
        {/* TÜV information */}
        <Box
          sx={{
            mt: { xs: 3.5, sm: 4.5 },
            p: { xs: 2, sm: 2.5 },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            flexWrap: "wrap",
            bgcolor: COLORS.white,
            border: `1px solid ${COLORS.border}`,
            borderRadius: 3,
          }}
        >
          <Box
            component="img"
            src={TuvSud}
            alt="TÜV SÜD"
            loading="lazy"
            sx={{
              width: 58,
              height: 58,
              objectFit: "contain",
              flexShrink: 0,
            }}
          />

          <Typography
            variant="body2"
            sx={{
              flex: "1 1 240px",
              color: COLORS.muted,
              lineHeight: 1.75,
              textAlign: { xs: "center", sm: "left" },
            }}
          >
            Информацията за сертификати, биоразградимост и други технически
            характеристики следва да се проверява според документацията на
            съответния продукт.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
